import { DEFAULT_TIMEZONE } from "@dream/config/constants";
import type { CalendarView, ResolvedOccurrence } from "@dream/domain";
import { IconGoogle, IconVideo } from "@dream/ui";
import type { EventApi, EventContentArg } from "@fullcalendar/core";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import luxon3Plugin from "@fullcalendar/luxon3";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { DateTime } from "luxon";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AvatarStack } from "../components/avatar-stack.tsx";
import {
  EventHoverCard,
  googleHoverModelFromEvent,
  hoverModelFromProps,
  useEventHover,
} from "../components/event-hover-card.tsx";
import { TypeIcon } from "../components/type-icon.tsx";
import { UpcomingPill } from "../components/upcoming-pill.tsx";
import { useCalendarDensity, useTimelineDays } from "../hooks/use-viewport.ts";
import { chipCssVars } from "../lib/chip-contrast.ts";
import {
  focusGuardHours,
  resolvedTypeColor,
  slotBounds,
  toBusinessHours,
} from "../preferences/preferences.ts";
import { usePreferences } from "../preferences/provider.tsx";
import { fromFullCalendarMarker } from "./adapter/fullcalendar-date.ts";
import {
  type CalendarCommand,
  type ClientMeta,
  clickToCommand,
  compareCalendarEventInputs,
  compareCalendarEvents,
  contextToCommand,
  type DreamEventProps,
  dropToCommand,
  type GoogleEventProps,
  type GoogleOverlayEvent,
  isGoogleOverlay,
  type ProjectMeta,
  readProps,
  resizeToCommand,
  selectToCommand,
  type TypeMeta,
  toEventInputs,
  toFullCalendarView,
  toGoogleEventInputs,
  visibleGoogleOverlay,
} from "./adapter/mapping.ts";
import { OperationalTimeline } from "./timeline.tsx";
import "./dream-calendar.css";

/**
 * Camada Dream Calendar em volta do FullCalendar.
 *
 * O FullCalendar entra como motor de layout e interação. Toda a aparência é
 * sobrescrita em `dream-calendar.css`, e o conteúdo de cada evento é renderizado
 * por componente próprio via `eventContent` — nenhum pixel do tema padrão fica
 * visível.
 *
 * A view `linha` não passa pelo plugin: `resourceTimeline` é FullCalendar
 * Premium (licença comercial) e o briefing restringe o projeto a plugins
 * gratuitos. Ver docs/repository-research.md.
 */

export interface DreamCalendarProps {
  view: CalendarView;
  occurrences: ResolvedOccurrence[];
  types: TypeMeta[];
  clients: ClientMeta[];
  timezone: string;
  /** Data de referência da navegação. */
  focusDate: Date;
  colorBy?: "type" | "client";
  groupBy?: "none" | "client" | "project" | "assignee";
  /** `false` no portal do cliente: sem arraste, sem resize, sem seleção. */
  interactive: boolean;
  loading?: boolean;
  onCommand: (command: CalendarCommand) => void;
  onRangeChange?: (range: { start: Date; end: Date }) => void;
  projects?: Array<{ id: string; name: string; color: string }>;
  team?: Array<{
    id: string;
    name: string;
    email?: string | null;
    image?: string | null;
    avatarColor?: string | null;
  }>;
  /**
   * Eventos da Google Agenda da equipe. Overlay só no painel interno — o
   * portal do cliente não passa esta lista.
   */
  googleEvents?: GoogleOverlayEvent[];
  /** Overlay da Agenda em refetch — não bloqueia a grade. */
  syncing?: boolean;
}

/**
 * Atraso cronológico curto para a entrada dos chips.
 *
 * Só vale na troca de view/período (`data-motion="enter"`). O teto evita uma
 * cascata longa em meses cheios.
 */
function chipStaggerMs(arg: EventContentArg): number {
  const original = arg.event.extendedProps.originalStartsAt;
  const start =
    typeof original === "string" && original.length > 0 ? new Date(original) : arg.event.start;
  const viewStart = arg.view.activeStart;
  if (start === null || Number.isNaN(start.getTime())) return 0;
  const delta = Math.max(0, start.getTime() - viewStart.getTime());
  const type = arg.view.type;
  if (type === "dayGridMonth") {
    return Math.min(Math.floor(delta / 86_400_000), 10) * 18;
  }
  if (type.startsWith("list")) {
    return Math.min(Math.floor(delta / 86_400_000), 8) * 22;
  }
  return Math.min(Math.floor(delta / 1_800_000), 12) * 16;
}

function formatOriginalClock(iso: string): string | null {
  const dt = DateTime.fromISO(iso, { zone: DEFAULT_TIMEZONE });
  if (!dt.isValid) return null;
  return dt.toFormat("HH:mm");
}

/** Ofative 56:789: `08:00AM - 09:00AM`. Mantém zero à esquerda; 24h no produto. */
function formatChipRange(timeText: string): string {
  return timeText.replace(/\s*[-–—]\s*/u, " – ");
}

function gmtCssLabel(timezone: string): string {
  const label = DateTime.now().setZone(timezone).toFormat("ZZZZ").replace("UTC", "GMT");
  return `"${label}"`;
}

function chipMotionStyle(
  color: string,
  arg: EventContentArg,
  foreground?: string | null,
): CSSProperties {
  return {
    ...chipCssVars(color, foreground),
    ["--dream-stagger" as string]: `${chipStaggerMs(arg)}ms`,
  } as CSSProperties;
}

function useViewMotion(view: CalendarView, focusDate: Date): "enter" | "idle" {
  const [motion, setMotion] = useState<"enter" | "idle">("enter");
  const key = `${view}:${focusDate.getTime()}`;

  useEffect(() => {
    setMotion("enter");
    const timer = window.setTimeout(() => setMotion("idle"), key.length > 0 ? 650 : 0);
    return () => window.clearTimeout(timer);
  }, [key]);

  return motion;
}

function EventChip({ arg }: { arg: EventContentArg }) {
  const raw = arg.event.extendedProps as Record<string, unknown>;
  if (isGoogleOverlay(raw)) {
    return <GoogleEventChip arg={arg} props={raw as unknown as GoogleEventProps} />;
  }
  const props = raw as unknown as DreamEventProps;
  const isList = arg.view.type.startsWith("list");
  const isMonth = arg.view.type === "dayGridMonth";
  const color = arg.event.backgroundColor;
  const motionStyle = chipMotionStyle(color, arg);
  const people = Array.isArray(props.people) ? props.people : [];

  const time = arg.event.allDay ? "Dia inteiro" : formatChipRange(arg.timeText);

  const glyph = (
    <span className="dream-chip__glyph" aria-hidden="true">
      <TypeIcon slug={props.typeSlug} name={props.typeIcon} size={isList ? 16 : 14} />
    </span>
  );

  if (isList) {
    return (
      <div
        className="dream-chip dream-chip--list"
        data-type={props.typeSlug ?? undefined}
        data-late={props.isLate ? "true" : undefined}
        style={motionStyle}
      >
        {glyph}
        <span className="dream-chip__body">
          <span className="dream-chip__title">{arg.event.title}</span>
          <span className="dream-chip__time">{time}</span>
        </span>
        <AvatarStack people={people} />
      </div>
    );
  }

  if (isMonth) {
    return (
      <div
        className="dream-chip dream-chip--dense"
        style={motionStyle}
        data-late={props.isLate ? "true" : undefined}
        data-type={props.typeSlug ?? undefined}
      >
        {!arg.event.allDay ? <span className="dream-chip__time">{time.split("–")[0]}</span> : null}
        {glyph}
        <span className="dream-chip__title">{arg.event.title}</span>
        <AvatarStack people={people} size={16} />
      </div>
    );
  }

  return (
    <div
      className="dream-chip"
      style={motionStyle}
      data-late={props.isLate ? "true" : undefined}
      data-type={props.typeSlug ?? undefined}
    >
      <span className="dream-chip__head">
        <span className="dream-chip__title-row">
          {glyph}
          <span className="dream-chip__title">{arg.event.title}</span>
        </span>
        {!arg.event.allDay ? <span className="dream-chip__time">{time}</span> : null}
      </span>
      <AvatarStack people={people} />
    </div>
  );
}

function GoogleEventChip({ arg, props }: { arg: EventContentArg; props: GoogleEventProps }) {
  const isList = arg.view.type.startsWith("list");
  const isMonth = arg.view.type === "dayGridMonth";
  const color = arg.event.backgroundColor;
  const motionStyle = chipMotionStyle(color, arg, props.foreground);
  const originalClock =
    !props.originalAllDay && props.originalStartsAt.length > 0
      ? formatOriginalClock(props.originalStartsAt)
      : null;
  const time = arg.event.allDay ? "Dia inteiro" : formatChipRange(arg.timeText);
  const people = Array.isArray(props.people) ? props.people : [];
  const hasMeet = props.hangoutLink !== null && props.hangoutLink !== "";
  let chipClass = "dream-chip";
  if (isList) chipClass = "dream-chip dream-chip--list";
  else if (isMonth) chipClass = "dream-chip dream-chip--dense";

  const glyph = (
    <span className="dream-chip__glyph" aria-hidden="true">
      {hasMeet ? (
        <IconVideo className={isList ? "size-4" : "size-3.5"} />
      ) : (
        <IconGoogle className={isList ? "size-4" : "size-3.5"} />
      )}
    </span>
  );

  if (isList) {
    return (
      <div className={chipClass} style={motionStyle} data-source="google">
        {glyph}
        <span className="dream-chip__body">
          <span className="dream-chip__title">{arg.event.title}</span>
          <span className="dream-chip__time">
            {time}
            {props.calendarName !== "" ? ` · ${props.calendarName}` : ""}
          </span>
        </span>
        <AvatarStack people={people} />
      </div>
    );
  }

  if (isMonth) {
    return (
      <div className="dream-chip dream-chip--dense" style={motionStyle} data-source="google">
        {originalClock !== null ? <span className="dream-chip__time">{originalClock}</span> : null}
        {glyph}
        <span className="dream-chip__title">{arg.event.title}</span>
        <AvatarStack people={people} size={16} />
      </div>
    );
  }

  return (
    <div className={chipClass} style={motionStyle} data-source="google">
      <span className="dream-chip__head">
        <span className="dream-chip__title-row">
          {glyph}
          <span className="dream-chip__title">{arg.event.title}</span>
        </span>
        {time !== "Dia inteiro" ? <span className="dream-chip__time">{time}</span> : null}
      </span>
      <AvatarStack people={people} />
    </div>
  );
}

export function DreamCalendar({
  view,
  occurrences,
  types,
  clients,
  timezone,
  focusDate,
  colorBy = "type",
  groupBy = "none",
  interactive,
  loading = false,
  onCommand,
  onRangeChange,
  projects = [],
  team = [],
  googleEvents = [],
  syncing = false,
}: DreamCalendarProps) {
  const calendarRef = useRef<FullCalendar | null>(null);
  const hover = useEventHover();
  const timelineDays = useTimelineDays();
  const density = useCalendarDensity();
  const motion = useViewMotion(view, focusDate);
  const { prefs } = usePreferences();
  const slots = slotBounds(prefs);
  const hour12 = prefs.timeFormat === "12";
  const snapDuration =
    prefs.defaultDurationMin === 60
      ? "01:00:00"
      : `00:${String(prefs.defaultDurationMin).padStart(2, "0")}:00`;

  const typeMap = useMemo(
    () =>
      new Map(
        types.map((type) => [
          type.id,
          { ...type, color: resolvedTypeColor(type.id, type.color, prefs) },
        ]),
      ),
    [types, prefs],
  );
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const projectMap = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects],
  );
  const teamMap = useMemo(() => new Map(team.map((member) => [member.id, member])), [team]);

  const canDrag = interactive && prefs.allowDrag;

  const upcoming = useMemo(() => {
    if (!prefs.menubarUpcoming) return null;
    const now = Date.now();
    const zoneNow = DateTime.now().setZone(timezone);
    const horizon =
      prefs.upcomingHorizon === "today"
        ? zoneNow.endOf("day").toMillis()
        : zoneNow.plus({ days: 7 }).toMillis();
    const next = occurrences
      .filter(
        (item) =>
          item.startsAt.getTime() >= now &&
          item.startsAt.getTime() <= horizon &&
          item.status !== "cancelled",
      )
      .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime())[0];
    if (next === undefined) return null;
    const assignee = next.assigneeId === null ? undefined : teamMap.get(next.assigneeId);
    return {
      deliverableId: next.deliverableId,
      title: next.title,
      startsAt: next.startsAt,
      clientName: clientMap.get(next.clientId)?.name ?? null,
      assigneeName: assignee?.name ?? null,
      people:
        assignee === undefined
          ? []
          : [
              {
                id: assignee.id,
                name: assignee.name,
                imageUrl: assignee.image ?? null,
                color: assignee.avatarColor ?? null,
              },
            ],
    };
  }, [occurrences, clientMap, teamMap, prefs.menubarUpcoming, prefs.upcomingHorizon, timezone]);

  const overlay = useMemo(() => visibleGoogleOverlay(googleEvents), [googleEvents]);

  const events = useMemo(() => {
    const mapped = [
      ...toEventInputs(occurrences, {
        types: typeMap,
        clients: clientMap,
        projects: projectMap,
        team: teamMap,
        colorBy,
        interactive: canDrag,
      }),
      ...toGoogleEventInputs(overlay, { pinToCivilDay: view === "mes", team: teamMap }),
    ].sort(compareCalendarEventInputs);
    const focus = focusGuardHours(prefs);
    if (focus === null) return mapped;
    return [
      ...mapped,
      {
        id: "dream-focus-guard",
        display: "background" as const,
        startTime: focus.startTime,
        endTime: focus.endTime,
        daysOfWeek: focus.daysOfWeek,
        backgroundColor: "#D0E5FF",
        classNames: ["dream-focus-guard"],
      },
    ];
  }, [
    occurrences,
    overlay,
    typeMap,
    clientMap,
    projectMap,
    teamMap,
    colorBy,
    canDrag,
    prefs,
    view,
  ]);

  const fcView = toFullCalendarView(view);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api === undefined || fcView === null) return;
    if (api.view.type !== fcView) api.changeView(fcView);
    api.gotoDate(focusDate);
  }, [fcView, focusDate]);

  const handleDatesSet = useCallback(
    (arg: { start: Date; end: Date }) => {
      onRangeChange?.({ start: arg.start, end: arg.end });
    },
    [onRangeChange],
  );

  // A timeline operacional é implementação própria: nenhum plugin premium.
  if (fcView === null) {
    return (
      <div
        className="dream-calendar"
        data-view={view}
        data-density={density}
        data-slots={prefs.density}
        data-motion={motion}
        data-loading={loading ? "true" : undefined}
        data-syncing={syncing ? "true" : undefined}
        style={{ ["--dream-tz-label" as string]: gmtCssLabel(timezone) }}
      >
        {syncing ? <CalendarSyncBar /> : null}
        <OperationalTimeline
          occurrences={occurrences}
          types={types}
          clients={clients}
          timezone={timezone}
          focusDate={focusDate}
          groupBy={groupBy === "none" ? "client" : groupBy}
          interactive={interactive}
          loading={loading}
          onCommand={onCommand}
          days={timelineDays}
          projects={projects}
          team={team}
          onHover={hover.show}
          onHoverLeave={hover.hide}
          googleEvents={overlay}
        />
        <EventHoverCard leaving={hover.leaving} model={hover.model} />
        {interactive && view !== "mes" && view !== "agenda" ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3 max-[399px]:bottom-[5.5rem]">
            <UpcomingPill
              occurrence={upcoming}
              timezone={timezone}
              onOpen={(deliverableId) => {
                const hit = occurrences.find((item) => item.deliverableId === deliverableId);
                if (hit === undefined) return;
                onCommand({
                  kind: "open",
                  deliverableId,
                  occurrenceStart: hit.occurrenceStart.toISOString(),
                  title: hit.title,
                });
              }}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="dream-calendar"
      data-view={view}
      data-density={density}
      data-slots={prefs.density}
      data-motion={motion}
      data-loading={loading ? "true" : undefined}
      data-syncing={syncing ? "true" : undefined}
      style={{ ["--dream-tz-label" as string]: gmtCssLabel(timezone) }}
    >
      {syncing ? <CalendarSyncBar /> : null}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, luxon3Plugin]}
        initialView={fcView}
        initialDate={focusDate}
        timeZone={timezone}
        locale={ptBrLocale}
        dayHeaderContent={(arg) => {
          const dt = fromFullCalendarMarker(arg.date, timezone).setLocale("pt-BR");
          if (view === "mes" || view === "agenda") {
            const label = dt.toFormat("ccc").replace(".", "");
            return density === "phone" ? label.slice(0, 3) : label;
          }
          return (
            <div className="dream-day-header" data-today={arg.isToday ? "true" : undefined}>
              <span className="dream-day-header__num">{dt.toFormat("d")}</span>
              <span className="dream-day-header__dow">{dt.toFormat("ccc").replace(".", "")}</span>
            </div>
          );
        }}
        // Cabeçalho e navegação são componentes próprios; o do plugin é
        // desligado para não haver dois controles concorrentes.
        headerToolbar={false}
        height="100%"
        expandRows
        stickyHeaderDates
        dayMaxEvents={density === "phone" ? 4 : 8}
        eventMaxStack={
          view === "dia"
            ? density === "phone"
              ? 5
              : 6
            : view === "semana"
              ? density === "phone"
                ? 3
                : 4
              : undefined
        }
        eventMinHeight={prefs.density === "compact" ? 28 : 36}
        eventShortHeight={prefs.density === "compact" ? 48 : 64}
        slotEventOverlap={false}
        eventOrder={(a, b) => compareCalendarEvents(a as EventApi, b as EventApi)}
        eventOrderStrict
        moreLinkText={(n) => `+${n}`}
        firstDay={prefs.weekStartsOn}
        weekends={prefs.showWeekends}
        businessHours={view === "mes" || view === "agenda" ? false : toBusinessHours(prefs)}
        nowIndicator
        slotMinTime={slots.min}
        slotMaxTime={slots.max}
        slotDuration={prefs.density === "compact" ? "00:15:00" : "00:30:00"}
        snapDuration={snapDuration}
        defaultTimedEventDuration={snapDuration}
        slotLabelInterval="01:00:00"
        slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12 }}
        eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12 }}
        allDayText="Dia inteiro"
        noEventsText="Nada neste período"
        listDayFormat={{ weekday: "long", day: "numeric", month: "long" }}
        listDaySideFormat={false}
        events={events}
        eventContent={(arg) => <EventChip arg={arg} />}
        eventDidMount={(info) => {
          info.el.removeAttribute("title");
          if (!interactive) return;
          if (isGoogleOverlay(info.event.extendedProps)) return;
          info.el.addEventListener("contextmenu", (event) => {
            event.preventDefault();
            const command = contextToCommand({
              eventId: info.event.id,
              title: info.event.title,
              start: info.event.start,
              end: info.event.end,
              allDay: info.event.allDay,
              backgroundColor: info.event.backgroundColor,
              extendedProps: info.event.extendedProps,
              x: event.clientX,
              y: event.clientY,
              timezone,
            });
            if (command !== null) onCommand(command);
          });
        }}
        eventMouseEnter={(info) => {
          const raw = info.event.extendedProps;
          const start = info.event.start;
          const end = info.event.end;
          if (isGoogleOverlay(raw)) {
            const google = raw as unknown as GoogleEventProps;
            const allDay = google.originalAllDay;
            const originStart = DateTime.fromISO(google.originalStartsAt, { zone: timezone });
            const originEnd = DateTime.fromISO(google.originalEndsAt, { zone: timezone });
            hover.show(
              googleHoverModelFromEvent({
                title: info.event.title,
                props: google,
                color: info.event.backgroundColor,
                allDay,
                timeText:
                  allDay || !originStart.isValid
                    ? "Dia inteiro"
                    : !originEnd.isValid
                      ? originStart.toFormat("HH:mm")
                      : `${originStart.toFormat("HH:mm")}–${originEnd.toFormat("HH:mm")}`,
                el: info.el,
              }),
            );
            return;
          }
          const props = readProps(raw);
          if (props === null) return;
          const zone = props.timezone;
          const timeText =
            info.event.allDay || start === null
              ? "Dia inteiro"
              : end === null
                ? fromFullCalendarMarker(start, zone).toFormat("HH:mm")
                : `${fromFullCalendarMarker(start, zone).toFormat("HH:mm")}–${fromFullCalendarMarker(end, zone).toFormat("HH:mm")}`;
          hover.show(
            hoverModelFromProps({
              title: info.event.title,
              props,
              color: info.event.backgroundColor,
              allDay: info.event.allDay,
              timeText,
              el: info.el,
            }),
          );
        }}
        eventMouseLeave={() => hover.hide()}
        eventDragStart={() => hover.clear()}
        eventResizeStart={() => hover.clear()}
        // Interatividade é decidida pelo servidor, não pela UI: aqui só
        // refletimos. Toda mutação é revalidada na API.
        editable={canDrag}
        eventStartEditable={canDrag}
        eventResizableFromStart={false}
        eventDurationEditable={canDrag}
        selectable={interactive}
        selectMirror={interactive}
        droppable={false}
        eventDrop={(arg) => {
          const command = dropToCommand({
            eventId: arg.event.id,
            extendedProps: arg.event.extendedProps,
            start: arg.event.start,
            end: arg.event.end,
            allDay: arg.event.allDay,
          });
          if (command === null) {
            // Sem comando válido não há como persistir: desfaz na hora em vez
            // de deixar a UI mostrando um estado que o servidor não conhece.
            arg.revert();
            return;
          }
          onCommand({ ...command, revert: arg.revert });
        }}
        eventResize={(arg) => {
          const command = resizeToCommand({
            extendedProps: arg.event.extendedProps,
            end: arg.event.end,
          });
          if (command === null) {
            arg.revert();
            return;
          }
          onCommand({ ...command, revert: arg.revert });
        }}
        eventClick={(arg) => {
          const raw = arg.event.extendedProps;
          if (isGoogleOverlay(raw)) {
            const link = (raw as unknown as GoogleEventProps).htmlLink;
            if (typeof link === "string" && link !== "") {
              window.open(link, "_blank", "noopener,noreferrer");
            }
            return;
          }
          const command = clickToCommand({
            eventId: arg.event.id,
            title: arg.event.title,
            extendedProps: raw,
          });
          if (command !== null) onCommand(command);
        }}
        select={(arg) => {
          onCommand(
            selectToCommand({
              start: arg.start,
              end: arg.end,
              allDay: arg.allDay,
              timezone,
            }),
          );
          calendarRef.current?.getApi().unselect();
        }}
        datesSet={handleDatesSet}
        eventClassNames={(arg) => {
          if (isGoogleOverlay(arg.event.extendedProps)) return ["dream-event--google"];
          const props = arg.event.extendedProps as unknown as DreamEventProps;
          return props.isOverride ? ["dream-event--override"] : [];
        }}
      />
      <EventHoverCard leaving={hover.leaving} model={hover.model} />
      {interactive && view !== "mes" && view !== "agenda" ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3 max-[399px]:bottom-[5.5rem]">
          <UpcomingPill
            occurrence={upcoming}
            timezone={timezone}
            onOpen={(deliverableId) => {
              const hit = occurrences.find((item) => item.deliverableId === deliverableId);
              if (hit === undefined) return;
              onCommand({
                kind: "open",
                deliverableId,
                occurrenceStart: hit.occurrenceStart.toISOString(),
                title: hit.title,
              });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

export type { CalendarCommand, ClientMeta, ProjectMeta, TypeMeta };

function CalendarSyncBar() {
  return (
    <div className="dream-calendar__sync" role="status" aria-live="polite">
      <span className="sr-only">Sincronizando a Google Agenda</span>
    </div>
  );
}
